import { forwardRef, useCallback } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Represents a single subcategory tag option
 */
export interface SubcategoryTag {
  /**
   * Unique identifier for the tag
   */
  id: string;
  /**
   * Display label for the tag
   */
  label: string;
}

/**
 * Props for the SubcategoryTags component
 */
export interface SubcategoryTagsProps {
  /**
   * Array of tags to display
   */
  tags: SubcategoryTag[];
  /**
   * Array of currently selected tag IDs
   */
  selectedIds: string[];
  /**
   * Callback fired when selection changes
   * @param selectedIds - Updated array of selected tag IDs
   */
  onChange: (selectedIds: string[]) => void;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
}

/**
 * SubcategoryTags - Selectable tag chips for category filtering
 *
 * A controlled component that displays a list of selectable tag chips.
 * Supports multiple selections with visual feedback for selected state.
 *
 * @example
 * ```tsx
 * const [selectedIds, setSelectedIds] = useState<string[]>(['tag-1']);
 *
 * <SubcategoryTags
 *   tags={[
 *     { id: 'tag-1', label: 'Housing' },
 *     { id: 'tag-2', label: 'Food' },
 *     { id: 'tag-3', label: 'Healthcare' },
 *   ]}
 *   selectedIds={selectedIds}
 *   onChange={setSelectedIds}
 * />
 * ```
 */
export const SubcategoryTags = forwardRef<HTMLDivElement, SubcategoryTagsProps>(
  ({ tags, selectedIds, onChange, className }, ref) => {
    const handleTagClick = useCallback(
      (tagId: string) => {
        const isSelected = selectedIds.includes(tagId);
        if (isSelected) {
          // Remove from selection
          onChange(selectedIds.filter((id) => id !== tagId));
        } else {
          // Add to selection
          onChange([...selectedIds, tagId]);
        }
      },
      [selectedIds, onChange]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>, tagId: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleTagClick(tagId);
        }
      },
      [handleTagClick]
    );

    if (!tags || tags.length === 0) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn('flex flex-wrap gap-2', className)}
        role="group"
        aria-label="Filter by subcategory"
      >
        {tags.map((tag) => {
          const isSelected = selectedIds.includes(tag.id);

          return (
            <button
              key={tag.id}
              type="button"
              role="switch"
              aria-checked={isSelected}
              onClick={() => handleTagClick(tag.id)}
              onKeyDown={(e) => handleKeyDown(e, tag.id)}
              className={cn(
                // Base styles
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium',
                'transition-colors duration-150 ease-in-out',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                // State-based styles
                isSelected
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {isSelected && (
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <span>{tag.label}</span>
            </button>
          );
        })}
      </div>
    );
  }
);

SubcategoryTags.displayName = 'SubcategoryTags';
